import { Icon, Textfield, token } from "@may-i/mash-design-system";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { PageSpinner } from "~/shared/ui/PageSpinner";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { Branch } from "~/entities/branch/types";
import { useBranches } from "~/features/branch/hooks/use-branches";
import { usePinnedClients } from "~/features/branch/hooks/use-pinned-items";
import {
  BranchRowWithVersions,
  ClientHeaderRow,
  TableHeader,
} from "./BranchTable";

interface ClientGroup {
  clientUid: string;
  clientName: string;
  branches: Branch[];
}

type FlatRow =
  | {
      kind: "client-header";
      clientUid: string;
      clientName: string;
      isPinned: boolean;
      isCollapsed: boolean;
      isLastInGroup: boolean;
    }
  | {
      kind: "branch";
      clientUid: string;
      branch: Branch;
      isLastInGroup: boolean;
    };

const COLLAPSED_STORAGE_KEY = "branch-management:collapsed-clients";
const CLIENT_HEADER_HEIGHT = 38;
const BRANCH_ROW_HEIGHT = 54;
const GROUP_GAP = 12;

function readCollapsedClients(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const stored = window.localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (!stored) return new Set();
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? new Set<string>(parsed) : new Set();
  } catch {
    return new Set();
  }
}

function writeCollapsedClients(set: Set<string>) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      COLLAPSED_STORAGE_KEY,
      JSON.stringify(Array.from(set)),
    );
  } catch {
    // ignore quota / disabled storage
  }
}

export function AdminBranchView() {
  const { t } = useTranslation("branch-management");
  const { data: branches = [], isPending: isBranchesPending } = useBranches();
  const { isPinned, toggle: togglePin } = usePinnedClients();
  const [search, setSearch] = useState("");
  const [collapsedClients, setCollapsedClients] = useState<Set<string>>(
    readCollapsedClients,
  );

  useEffect(() => {
    writeCollapsedClients(collapsedClients);
  }, [collapsedClients]);

  const toggleCollapse = useCallback((clientUid: string) => {
    setCollapsedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientUid)) {
        next.delete(clientUid);
      } else {
        next.add(clientUid);
      }
      return next;
    });
  }, []);

  const sortedGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? branches.filter(
          (b) =>
            b.name.toLowerCase().includes(query) ||
            b.client.name.toLowerCase().includes(query),
        )
      : branches;

    const groupMap = new Map<string, ClientGroup>();
    for (const branch of filtered) {
      const existing = groupMap.get(branch.client.uid);
      if (existing) {
        existing.branches.push(branch);
      } else {
        groupMap.set(branch.client.uid, {
          clientUid: branch.client.uid,
          clientName: branch.client.name,
          branches: [branch],
        });
      }
    }

    const groups = Array.from(groupMap.values());
    const pinned: ClientGroup[] = [];
    const unpinned: ClientGroup[] = [];
    for (const group of groups) {
      if (isPinned(group.clientUid)) {
        pinned.push(group);
      } else {
        unpinned.push(group);
      }
    }
    return [...pinned, ...unpinned];
  }, [branches, search, isPinned]);

  const flatRows = useMemo<FlatRow[]>(() => {
    const rows: FlatRow[] = [];
    for (const group of sortedGroups) {
      const isCollapsed = collapsedClients.has(group.clientUid);
      const childBranches = isCollapsed ? [] : group.branches;
      rows.push({
        kind: "client-header",
        clientUid: group.clientUid,
        clientName: group.clientName,
        isPinned: isPinned(group.clientUid),
        isCollapsed,
        isLastInGroup: childBranches.length === 0,
      });
      childBranches.forEach((branch, idx) => {
        rows.push({
          kind: "branch",
          clientUid: group.clientUid,
          branch,
          isLastInGroup: idx === childBranches.length - 1,
        });
      });
    }
    return rows;
  }, [sortedGroups, collapsedClients, isPinned]);

  const isAllCollapsed =
    sortedGroups.length > 0 &&
    sortedGroups.every((g) => collapsedClients.has(g.clientUid));

  const toggleCollapseAll = useCallback(() => {
    if (isAllCollapsed) {
      setCollapsedClients(new Set());
    } else {
      setCollapsedClients(
        new Set(sortedGroups.map((g) => g.clientUid)),
      );
    }
  }, [isAllCollapsed, sortedGroups]);

  const parentRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    if (!parentRef.current) return;
    const update = () => {
      if (!parentRef.current) return;
      const rect = parentRef.current.getBoundingClientRect();
      setScrollMargin(rect.top + window.scrollY);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const estimateSize = useCallback(
    (index: number) => {
      const row = flatRows[index];
      const baseHeight =
        row.kind === "client-header"
          ? CLIENT_HEADER_HEIGHT
          : BRANCH_ROW_HEIGHT;
      return row.isLastInGroup ? baseHeight + GROUP_GAP : baseHeight;
    },
    [flatRows],
  );

  const virtualizer = useWindowVirtualizer({
    count: flatRows.length,
    estimateSize,
    overscan: 8,
    scrollMargin,
  });

  const virtualItems = virtualizer.getVirtualItems();

  if (isBranchesPending) {
    return <Container><PageSpinner /></Container>;
  }

  return (
    <Container>
      <StickyHeader>
        <TitleRow>
          <Title>{t("title")}</Title>
        </TitleRow>

        <HeaderContents>
          <SearchRow>
            <SearchInput
              size="small"
              placeholder={t("searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              startIcon="ic_search"
            />
            <CollapseAllButton onClick={toggleCollapseAll}>
              <CollapseAllIcon
                icon={isAllCollapsed ? "ic_chevron_down" : "ic_chevron_up"}
                width={16}
              />
              {isAllCollapsed ? t("expandAll") : t("collapseAll")}
            </CollapseAllButton>
          </SearchRow>

          <TableHeader />
        </HeaderContents>
      </StickyHeader>

      <Contents>
        {flatRows.length > 0 ? (
          <VirtualWrapper
            ref={parentRef}
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualItems.map((vItem) => {
              const row = flatRows[vItem.index];
              const offset = vItem.start - virtualizer.options.scrollMargin;
              const isFirstInGroup = row.kind === "client-header";
              return (
                <VirtualRow
                  key={vItem.key}
                  data-index={vItem.index}
                  ref={virtualizer.measureElement}
                  style={{ transform: `translateY(${offset}px)` }}
                >
                  <RowSurface
                    $roundTop={isFirstInGroup}
                    $roundBottom={row.isLastInGroup}
                    $gapBelow={row.isLastInGroup}
                  >
                    {row.kind === "client-header" ? (
                      <ClientHeaderRow
                        clientName={row.clientName}
                        isPinned={row.isPinned}
                        isCollapsed={row.isCollapsed}
                        onTogglePin={() => togglePin(row.clientUid)}
                        onToggleCollapse={() => toggleCollapse(row.clientUid)}
                      />
                    ) : (
                      <BranchRowWithVersions
                        branch={row.branch}
                        showFavorite={false}
                      />
                    )}
                  </RowSurface>
                </VirtualRow>
              );
            })}
          </VirtualWrapper>
        ) : (
          <EmptyRow>{t("empty")}</EmptyRow>
        )}
      </Contents>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StickyHeader = styled.div`
  position: sticky;
  top: var(--gnb-height, 64px);
  z-index: 10;
  background-color: #ffffff;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px 0;
`;

const HeaderContents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 24px 8px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const Contents = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px 100px;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CollapseAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${token("colors.text.secondary")};
  white-space: nowrap;

  &:hover {
    background-color: rgba(143, 149, 178, 0.12);
  }
`;

const CollapseAllIcon = styled(Icon)`
  color: ${token("colors.text.secondary")};
`;

const SearchInput = styled(Textfield)`
  width: 320px;

  & > div {
    padding: 4px 10px 4px 12px;
    gap: 4px;
    border-radius: 8px;
    outline: 1px solid rgba(143, 149, 178, 0.32);
    font-size: 14px;

    &:hover:not(.input__wrapper--focused) {
      outline: 1px solid rgba(143, 149, 178, 0.32);
    }
  }

  input::placeholder {
    color: #8f95b2;
  }
`;

const VirtualWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const VirtualRow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;

const RowSurface = styled.div<{
  $roundTop: boolean;
  $roundBottom: boolean;
  $gapBelow: boolean;
}>`
  background-color: #ffffff;
  border-top-left-radius: ${({ $roundTop }) => ($roundTop ? "8px" : "0")};
  border-top-right-radius: ${({ $roundTop }) => ($roundTop ? "8px" : "0")};
  border-bottom-left-radius: ${({ $roundBottom }) =>
    $roundBottom ? "8px" : "0"};
  border-bottom-right-radius: ${({ $roundBottom }) =>
    $roundBottom ? "8px" : "0"};
  overflow: hidden;
  margin-bottom: ${({ $gapBelow }) => ($gapBelow ? `${GROUP_GAP}px` : "0")};
`;

const EmptyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 12px;
  font-size: 14px;
  color: ${token("colors.text.secondary")};
`;
